import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, XCircle, Loader2, RefreshCw, TrendingUp, AlertTriangle } from "lucide-react";

const TYPE_ICONS = {
  frequent_delays: TrendingUp,
  driver_coaching: AlertTriangle,
  speeding_pattern: AlertTriangle,
};

const STATUS_COLORS = {
  open: "bg-[#FFC60A] text-zinc-900",
  applied: "bg-green-500 text-white",
  ignored: "bg-zinc-300 text-zinc-700",
};

export default function AIRecommendations() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("open");
  const [generating, setGenerating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/ai/recommendations${filter !== "all" ? `?status=${filter}` : ""}`);
      setItems(data);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post("/ai/recommendations/generate");
      toast.success(`${data.created} nouvelle(s) recommandation(s) (${data.total_seeds} signaux analysés)`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };

  const doAction = async (id, action) => {
    try {
      const { data } = await api.post(`/ai/recommendations/${id}/${action}`, { note: "" });
      const ar = data?.action;
      if (action === "apply") {
        if (ar?.status === "applied") {
          toast.success(`Appliquée — règle Logitrak créée (id=${ar.rule_id || "?"}, seuil ${ar.speed_kmh} km/h)`);
        } else if (ar?.status === "skipped") {
          toast.warning(`Recommandation marquée appliquée — Logitrak ignoré : ${ar.reason}`);
        } else if (ar?.status === "failed") {
          toast.error(`Échec Logitrak : ${ar.error || JSON.stringify(ar.response).slice(0, 90)}. Recommandation restée ouverte.`);
        } else {
          toast.success("Recommandation appliquée");
        }
      } else {
        toast.success("Recommandation ignorée");
      }
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || e.message); }
  };

  const counts = { open: 0, applied: 0, ignored: 0 };
  items.forEach((i) => { counts[i.status] = (counts[i.status] || 0) + 1; });

  return (
    <div className="p-4 lg:p-6" data-testid="ai-recommendations-page">
      <header className="flex items-end justify-between flex-wrap gap-3 mb-5">
        <div>
          <div className="text-[10px] font-mono tracking-[0.3em] uppercase text-zinc-500">IA · Optimisation</div>
          <h1 className="font-display font-black uppercase text-3xl lg:text-4xl tracking-tighter">Recommandations IA</h1>
          <p className="text-sm text-zinc-600 mt-1">
            L'IA analyse vos données (retards, scores, événements) et propose des optimisations concrètes.
          </p>
        </div>
        <button onClick={generate} disabled={generating} data-testid="generate-ai"
          className="inline-flex items-center gap-2 bg-zinc-900 text-[#FFC60A] border border-zinc-900 px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-zinc-800">
          {generating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Lancer l'analyse IA
        </button>
      </header>

      <div className="border border-zinc-900 grid grid-cols-4 mb-4">
        {[
          { k: "all", l: "Toutes", v: items.length },
          { k: "open", l: "À traiter", v: counts.open || 0 },
          { k: "applied", l: "Appliquées", v: counts.applied || 0 },
          { k: "ignored", l: "Ignorées", v: counts.ignored || 0 },
        ].map((tab) => (
          <button key={tab.k} onClick={() => setFilter(tab.k)} data-testid={`filter-${tab.k}`}
            className={`p-3 border-r border-zinc-900 last:border-r-0 text-left ${filter === tab.k ? "bg-[#FFF7D6]" : "bg-white"}`}>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{tab.l}</div>
            <div className="font-display font-black text-2xl tracking-tighter">{tab.v}</div>
          </button>
        ))}
      </div>

      <div className="space-y-3">
        {loading && <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 className="animate-spin" size={14} /> Chargement…</div>}
        {!loading && items.length === 0 && (
          <div className="border border-zinc-900 bg-white p-8 text-center">
            <Sparkles size={32} className="mx-auto text-zinc-300" />
            <div className="mt-2 font-display font-bold">Aucune recommandation</div>
            <div className="text-xs text-zinc-500 mt-1">Cliquez sur "Lancer l'analyse IA" pour générer des suggestions à partir de vos données.</div>
          </div>
        )}
        {items.map((rec) => {
          const Icon = TYPE_ICONS[rec.type] || Sparkles;
          return (
            <div key={rec.id} data-testid={`rec-${rec.id}`} className="border border-zinc-900 bg-white p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-[#FFF7D6] border border-zinc-900 flex items-center justify-center shrink-0">
                  <Icon size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] font-mono uppercase tracking-widest border border-zinc-900 px-1.5 py-0.5 ${STATUS_COLORS[rec.status]}`}>{rec.status}</span>
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{rec.type.replace("_", " ")}</span>
                  </div>
                  <div className="mt-1 font-display font-bold text-base">{rec.problem}</div>
                  <div className="mt-1 text-sm text-zinc-700">
                    <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 mr-1">→</span>
                    {rec.recommendation}
                  </div>
                  {rec.ai_insight && (
                    <div className="mt-2 border border-zinc-900 bg-[#FFF7D6] p-2 text-xs">
                      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-700 mr-1"><Sparkles size={10} className="inline" /> Claude AI</span>
                      {rec.ai_insight}
                    </div>
                  )}

                  {/* Enriched analytics (speeding_pattern, frequent_delays...) */}
                  {rec.details && (rec.details.top_drivers?.length > 0 || rec.details.top_routes?.length > 0 || rec.details.hot_hours?.length > 0) && (
                    <div className="mt-2 grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`rec-details-${rec.id}`}>
                      {rec.details.top_drivers?.length > 0 && (
                        <div className="border border-zinc-300 bg-zinc-50 p-2">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Top chauffeurs</div>
                          {rec.details.top_drivers.map((d, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                              <span className="font-medium truncate">{d.name}</span>
                              <span className="font-mono text-zinc-600 ml-2 shrink-0">{d.count}× · {d.share}%</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {rec.details.top_routes?.length > 0 && (
                        <div className="border border-zinc-300 bg-zinc-50 p-2">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Lignes concernées</div>
                          {rec.details.top_routes.map((r, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                              <span className="font-medium truncate">Ligne {r.label}</span>
                              <span className="font-mono text-zinc-600 ml-2 shrink-0">{r.count}×</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {rec.details.hot_hours?.length > 0 && (
                        <div className="border border-zinc-300 bg-zinc-50 p-2">
                          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Heures sensibles</div>
                          {rec.details.hot_hours.map((h, i) => (
                            <div key={i} className="flex items-center justify-between text-xs">
                              <span className="font-medium">{String(h.hour).padStart(2,"0")}h–{String(h.hour+1).padStart(2,"0")}h</span>
                              <span className="font-mono text-zinc-600 ml-2 shrink-0">{h.count}×</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {rec.action_result && rec.status === "applied" && (
                    <div className="mt-2 border border-emerald-700 bg-emerald-50 p-2 text-[11px] font-mono">
                      ✅ Action exécutée : règle Logitrak créée (id={rec.action_result.rule_id || "?"}, seuil {rec.action_result.speed_kmh || "?"} km/h) le {new Date(rec.applied_at).toLocaleString("fr-FR")}
                    </div>
                  )}
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-green-700">
                    Gain : {rec.estimated_gain}
                  </div>
                </div>
                {rec.status === "open" && (
                  <div className="flex flex-col gap-1">
                    <button onClick={() => doAction(rec.id, "apply")} data-testid={`apply-${rec.id}`}
                      className="inline-flex items-center gap-1 bg-zinc-900 text-[#FFC60A] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-800">
                      <CheckCircle2 size={11} /> Appliquer
                    </button>
                    <button onClick={() => doAction(rec.id, "ignore")} data-testid={`ignore-${rec.id}`}
                      className="inline-flex items-center gap-1 border border-zinc-900 px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest hover:bg-zinc-100">
                      <XCircle size={11} /> Ignorer
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
